import {
  allowedRates,
  checkGstin,
  commonRates,
  formatPaise,
  GST_STATES,
  invoiceNumberProblem,
  isInterState,
  isServiceCode,
  lineTax,
  rateForPrice,
  readRupees,
  retiredRate,
  type SupplyKind,
  supplyKind,
  taxableFromInclusive,
  tidyInvoiceNumber,
} from '@oneapp/gst';
import type { HsnResult, Invoice, ReturnDraft, SaleInput, Taxpayer } from '@oneapp/shared';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { businessApi, businessKeys } from '../business/api';
import { GstinField, useShortDate } from '../business/parts';
import { Icon } from '../components/icons';
import { Button, Callout } from '../components/ui';
import { ApiError } from '../lib/api';
import { salesApi, salesKeys } from './api';

/**
 * S08 · Add a sale, and V1 · Edit a sale: who bought (registered or a consumer), the invoice, what was
 * sold and for how much. The tax is worked out as you type, with the same rules the server checks;
 * the server's answer is what counts. In a GSTR-1A (gap 4b) it also corrects a filed sale: its buyer and
 * number stay as filed.
 */

type Kind = 'b2b' | 'b2c';
export type SavedHow = 'another' | 'review' | 'save';

interface Values {
  kind: Kind;
  gstin: string;
  number: string;
  date: string;
  hsn: string;
  hsnName: string;
  rate: string;
  quantity: string;
  amount: string;
  includesGst: boolean;
  delivery: string;
}

type Problems = Partial<
  Record<'gstin' | 'number' | 'date' | 'hsn' | 'rate' | 'quantity' | 'amount' | 'delivery', string>
>;

const pad = (n: number) => String(n).padStart(2, '0');

/** Today in India, as YYYY-MM-DD. */
export function indiaToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(now);
}

/** The month's last day, or today while it's still running: where a new sale's date starts. */
export function defaultDate(period: string, today = indiaToday()): string {
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(5, 7));
  const last = `${period}-${pad(new Date(Date.UTC(year, month, 0)).getUTCDate())}`;
  return last < today ? last : today;
}

function fromInvoice(invoice: Invoice): Values {
  const line = invoice.lines[0];
  return {
    kind: invoice.buyer ? 'b2b' : 'b2c',
    gstin: invoice.buyer?.gstin ?? '',
    number: invoice.numbers.join(', '),
    date: invoice.date,
    hsn: line?.hsn ?? '',
    hsnName: line?.description ?? '',
    rate: line ? String(line.rate) : '',
    quantity: line ? String(line.quantity || 1) : '1',
    amount: line ? String(line.taxableValue / 100) : '',
    includesGst: false,
    delivery: invoice.buyer ? '' : invoice.placeOfSupply,
  };
}

/** What a form can start from besides a sale: a row of an imported file, as far as it was read. */
export interface SaleFormStart {
  buyerGstin: string | null;
  number: string;
  date: string;
  hsn: string;
  description: string;
  rate: number | null;
  quantity: number | null;
  /** Paise. */
  taxableValue: number | null;
  placeOfSupply: string | null;
}

function fromStart(start: SaleFormStart): Values {
  return {
    kind: start.buyerGstin ? 'b2b' : 'b2c',
    gstin: start.buyerGstin ?? '',
    number: start.number,
    date: start.date,
    hsn: start.hsn,
    hsnName: start.description,
    rate: start.rate === null ? '' : String(start.rate),
    quantity: start.quantity ? String(start.quantity) : '1',
    amount: start.taxableValue === null ? '' : String(start.taxableValue / 100),
    includesGst: false,
    delivery: start.buyerGstin ? '' : (start.placeOfSupply ?? ''),
  };
}

const empty = (date: string, kind: Kind = 'b2b'): Values => ({
  kind,
  gstin: '',
  number: '',
  date,
  hsn: '',
  hsnName: '',
  rate: '',
  quantity: '1',
  amount: '',
  includesGst: false,
  delivery: '',
});

/** Where a server's field name points in the form. */
function fieldOf(name: string): keyof Problems | null {
  const key = name.replace(/^body\./, '');
  // A row of an imported file names its fields its own way.
  const row: Record<string, keyof Problems> = {
    invoiceNumber: 'number',
    invoiceDate: 'date',
    buyerGstin: 'gstin',
    hsn: 'hsn',
    rate: 'rate',
    quantity: 'quantity',
    taxableValue: 'amount',
  };
  if (row[key]) return row[key] as keyof Problems;
  if (key.startsWith('numbers')) return 'number';
  if (key === 'date') return 'date';
  if (key === 'buyerGstin') return 'gstin';
  if (key === 'placeOfSupply') return 'delivery';
  if (/^lines\.\d+\.rate$/.test(key)) return 'rate';
  if (/^lines\.\d+\.(hsn|uqc)$/.test(key)) return 'hsn';
  if (/^lines\.\d+\.quantity$/.test(key)) return 'quantity';
  if (/^lines\.\d+\.taxableValue$/.test(key)) return 'amount';
  return null;
}

/** Finding an HSN or SAC code: by its first digits or by what was sold. */
export function HsnField({
  value,
  name,
  date,
  error,
  onPick,
  onType,
}: {
  value: string;
  name: string;
  date: string;
  error?: string | undefined;
  onPick: (result: HsnResult) => void;
  onType: (code: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);
  const search = useQuery({
    queryKey: salesKeys.hsn(debounced, date),
    queryFn: ({ signal }) => salesApi.hsn(debounced, date, signal),
    enabled: open && debounced.length >= 2,
    staleTime: 10 * 60_000,
  });
  const results = search.data?.results ?? [];
  const listId = `${id}-list`;
  // Only point at the suggestions while they're there.
  const showList = open && debounced.length >= 2;
  const helpId = `${id}-help`;
  return (
    <div className="field hsn-field">
      <label className="field__label" htmlFor={id}>
        {t('sales.form.hsn')}
      </label>
      <input
        id={id}
        className="input"
        value={open ? query : value}
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        placeholder={t('sales.form.hsnPlaceholder')}
        aria-invalid={error ? true : undefined}
        aria-describedby={helpId}
        aria-controls={showList ? listId : undefined}
        onFocus={() => {
          setOpen(true);
          setQuery(value);
        }}
        onChange={(event) => {
          const text = event.target.value;
          setQuery(text);
          setOpen(true);
          if (/^\d{4}(\d{2}){0,2}$/.test(text.trim())) onType(text.trim());
        }}
        onBlur={() => {
          // Let a tap on a suggestion land first.
          setTimeout(() => setOpen(false), 150);
          if (/^\d{4}(\d{2}){0,2}$/.test(query.trim())) onType(query.trim());
        }}
      />
      <div id={helpId} aria-live="polite">
        {error ? (
          <p className="field__help field__help--error">{error}</p>
        ) : name && !open ? (
          <p className="field__help">{name}</p>
        ) : null}
      </div>
      {showList ? (
        <ul id={listId} className="hsn-field__list" aria-label={t('sales.form.hsnPick')}>
          {search.isFetching && !results.length ? (
            <li className="hsn-field__note">{t('sales.form.hsnSearching')}</li>
          ) : null}
          {!search.isFetching && !results.length ? (
            <li className="hsn-field__note">{t('sales.form.hsnNone')}</li>
          ) : null}
          {results.map((result) => (
            <li key={result.code}>
              <button
                type="button"
                className="hsn-field__option"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onPick(result);
                  setQuery(result.code);
                  setOpen(false);
                }}
              >
                <span className="hsn-field__code">{result.code}</span>
                <span className="hsn-field__name">{result.description}</span>
                {result.rate !== null ? <span className="hsn-field__rate">{result.rate}%</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** What's still wrong after saving somewhere else (a row of a file): by field, and anything else. */
export interface SaveProblems {
  fields: Record<string, string>;
  message: string | null;
}

export interface SaleFormProps {
  returnId: string;
  draft: ReturnDraft;
  sellerGstin: string;
  /** Editing this sale; adding a new one without. */
  invoice?: Invoice;
  /**
   * GSTR-1A (gap 4b): correcting this filed sale, with `invoice` the correction made so far. Its buyer and number
   * stay as filed; it's saved as a correction (PUT /returns/{id}/corrections/{filed sale}).
   */
  correcting?: Invoice;
  /** Starting from a row of an imported file instead of an empty form. */
  start?: SaleFormStart;
  /** The heading, when it isn't "Add a sale" or "Edit INV-…". */
  heading?: string;
  /** Under the heading: what needs fixing. */
  lead?: ReactNode;
  formId: string;
  onSaved?: (result: { invoice: Invoice; draft: ReturnDraft }, how: SavedHow) => void;
  /** Saves somewhere other than the draft (a row of a file); null when it's done, else what's still wrong. */
  save?: (input: SaleInput) => Promise<SaveProblems | null>;
  onDeleted?: (draft: ReturnDraft) => void;
  /** Tells the footer whether a save is under way. */
  onBusy?: (busy: boolean) => void;
}

export function SaleForm({
  returnId,
  draft,
  sellerGstin,
  invoice,
  correcting,
  start,
  heading,
  lead,
  formId,
  onSaved,
  save,
  onDeleted,
  onBusy,
}: SaleFormProps) {
  const { t } = useTranslation();
  const shortDate = useShortDate();
  const sellerState = sellerGstin.slice(0, 2);
  const [values, setValues] = useState<Values>(() =>
    invoice
      ? fromInvoice(invoice)
      : correcting
        ? fromInvoice(correcting)
        : start
          ? fromStart(start)
          : empty(defaultDate(draft.period)),
  );
  const [problems, setProblems] = useState<Problems>({});
  const [banner, setBanner] = useState<'conflict' | 'filed' | 'filing' | 'failed' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [rateTouched, setRateTouched] = useState(Boolean(invoice || correcting || start));
  const [priceRule, setPriceRule] = useState<HsnResult['priceRule']>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // One key per sale being added: a retry after a lost answer doesn't add it twice.
  const key = useRef(crypto.randomUUID());
  const firstField = useRef<HTMLInputElement>(null);
  const set = <K extends keyof Values>(field: K, value: Values[K]) => {
    setValues((v) => ({ ...v, [field]: value }));
    setProblems((p) => {
      const next = { ...p };
      delete next[field as keyof Problems];
      return next;
    });
  };
  useEffect(() => onBusy?.(busy), [busy, onBusy]);

  // The buyer, from the GST register, once the GSTIN is valid.
  const gstinCheck = values.kind === 'b2b' && values.gstin ? checkGstin(values.gstin) : null;
  const buyerGstin = gstinCheck?.ok ? gstinCheck.gstin : null;
  const lookup = useQuery({
    queryKey: businessKeys.lookup(buyerGstin ?? ''),
    queryFn: ({ signal }) => businessApi.lookup(buyerGstin ?? '', signal),
    enabled: Boolean(buyerGstin) && buyerGstin !== sellerGstin,
    retry: false,
    staleTime: 10 * 60_000,
  });
  const buyers = useQuery({
    queryKey: salesKeys.buyers(draft.businessId),
    queryFn: ({ signal }) => salesApi.buyers(draft.businessId, signal),
    staleTime: 60_000,
  });
  const taxpayer: Taxpayer | null = lookup.data?.taxpayer ?? null;
  const notRegistered = lookup.error instanceof ApiError && lookup.error.status === 404;
  const unchecked = lookup.error instanceof ApiError && lookup.error.status !== 404;

  // The tax, as the server will work it out.
  const nature = isServiceCode(values.hsn) ? 'services' : 'goods';
  const rate = values.rate === '' ? null : Number(values.rate);
  const entered = readRupees(values.amount);
  const quantity = Number(values.quantity) > 0 ? Number(values.quantity) : 1;
  const pos = values.kind === 'b2b' ? (buyerGstin?.slice(0, 2) ?? null) : values.delivery || sellerState;
  const inter = pos ? isInterState(sellerState, pos) : false;
  const taxable =
    entered !== null && rate !== null ? (values.includesGst ? taxableFromInclusive(entered, rate) : entered) : null;
  const tax = taxable !== null && rate !== null ? lineTax(taxable, rate, inter) : null;
  const invoiceValue = taxable !== null && tax ? taxable + tax.igst + tax.cgst + tax.sgst : null;
  const cancelled =
    taxpayer?.status === 'cancelled' && (!taxpayer.cancelledOn || !values.date || taxpayer.cancelledOn <= values.date);
  const table: SupplyKind | null =
    invoiceValue !== null && pos && values.date
      ? supplyKind({
          buyerGstin: values.kind === 'b2b' && !cancelled ? buyerGstin : null,
          interState: inter,
          invoiceValue,
          invoiceDate: values.date,
        })
      : null;

  // Clothes and shoes: 5% up to ₹2,500 a piece, 18% above. Follow the price unless a rate was picked.
  useEffect(() => {
    if (!priceRule || rateTouched || taxable === null || rate === null) return;
    const suggested = rateForPrice(Number(values.rate), priceRule, Math.round(taxable / quantity));
    if (suggested !== rate) setValues((v) => ({ ...v, rate: String(suggested) }));
  }, [priceRule, rateTouched, taxable, rate, quantity, values.rate]);

  const rateOptions = useMemo(() => {
    const date = values.date || defaultDate(draft.period);
    const common = commonRates(date);
    const rest = allowedRates(date, nature).filter((r) => !common.includes(r));
    return { common, rest };
  }, [values.date, draft.period, nature]);

  const money = (paise: number) => formatPaise(paise);
  const state = (code: string) => GST_STATES[code] ?? code;

  function check(): Problems {
    const found: Problems = {};
    const number = tidyInvoiceNumber(values.number);
    const numberProblem = invoiceNumberProblem(number);
    if (numberProblem) found.number = t(`sales.form.errors.number.${numberProblem}`);
    if (values.kind === 'b2b') {
      if (!gstinCheck?.ok) found.gstin = t('sales.form.errors.gstin');
      else if (buyerGstin === sellerGstin) found.gstin = t('sales.form.errors.ownGstin');
    }
    if (!values.date) found.date = t('sales.form.errors.required');
    else if (values.date > indiaToday()) found.date = t('sales.form.errors.future');
    if (!/^\d{4}(\d{2}){0,2}$/.test(values.hsn)) found.hsn = t('sales.form.errors.hsn');
    if (rate === null) found.rate = t('sales.form.errors.required');
    else if (values.date && retiredRate(rate, values.date, nature, values.hsn)) {
      found.rate = t(`sales.form.errors.retired.${retiredRate(rate, values.date, nature, values.hsn)}`);
    }
    if (entered === null) found.amount = t('sales.form.errors.amount');
    if (nature === 'goods' && !(Number(values.quantity) > 0)) found.quantity = t('sales.form.errors.quantity');
    return found;
  }

  function toInput(): SaleInput {
    const description = values.hsnName.trim();
    return {
      numbers: [tidyInvoiceNumber(values.number)],
      date: values.date,
      buyerGstin: values.kind === 'b2b' ? buyerGstin : null,
      ...(values.kind === 'b2c' && values.delivery && values.delivery !== sellerState
        ? { placeOfSupply: values.delivery }
        : {}),
      lines: [
        {
          hsn: values.hsn,
          ...(description ? { description: description.slice(0, 120) } : {}),
          ...(nature === 'services' ? {} : { quantity }),
          rate: rate ?? 0,
          taxableValue: taxable ?? 0,
        },
      ],
    };
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submitter = (event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
    const how: SavedHow = correcting
      ? submitter?.value === 'another'
        ? 'another'
        : 'review'
      : invoice
        ? 'save'
        : submitter?.value === 'review'
          ? 'review'
          : 'another';
    // A filed sale of more than one item: this form has one (EkApp can't correct those yet).
    if (correcting && correcting.lines.length > 1) return;
    const found = check();
    setProblems(found);
    setBanner(null);
    setMessage(null);
    if (Object.keys(found).length) return;
    setBusy(true);
    if (save) {
      try {
        const left = await save(toInput());
        if (left) {
          const mapped: Problems = {};
          const rest: string[] = [];
          for (const [name, text] of Object.entries(left.fields)) {
            const field = fieldOf(name);
            if (field) mapped[field] = mapped[field] ?? text;
            else rest.push(text);
          }
          setProblems(mapped);
          setMessage([left.message, ...rest].filter(Boolean).join(' ') || null);
        }
      } catch (error) {
        if (!(error instanceof ApiError)) throw error;
        setBanner('failed');
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      const result = correcting
        ? await salesApi.correct(returnId, correcting.id, toInput(), invoice?.version ?? null)
        : invoice
          ? await salesApi.save(returnId, invoice.id, toInput(), invoice.version)
          : await salesApi.add(returnId, toInput(), key.current);
      const saved = { invoice: result.invoice, draft: result.return };
      if (!invoice && !correcting) {
        key.current = crypto.randomUUID();
        if (how === 'another') {
          // Ready for the next one: same kind of buyer and date, the rest empty.
          setValues((v) => empty(v.date, v.kind));
          setRateTouched(false);
          setPriceRule(null);
          setToast(t('sales.form.savedToast', { number: result.invoice.numbers[0] }));
          firstField.current?.focus();
        }
      }
      onSaved?.(saved, how);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      const code = error.problem?.code;
      if (code === 'VERSION_MISMATCH') setBanner('conflict');
      // GSTR-1A closed meanwhile (GSTR-3B filed, say): the server says why.
      else if (code === 'GSTR1A_CLOSED') setMessage(error.problem?.detail ?? t('sales.form.failed'));
      else if (code === 'RETURN_FILED') setBanner('filed');
      else if (code === 'RETURN_BEING_FILED') setBanner('filing');
      else if (code === 'INVOICE_NUMBER_TAKEN')
        setProblems({ number: error.problem?.detail ?? t('sales.form.errors.taken') });
      else if (error.problem?.fields) {
        const mapped: Problems = {};
        for (const [name, message] of Object.entries(error.problem.fields)) {
          const field = fieldOf(name);
          if (field) mapped[field] = mapped[field] ?? message;
        }
        if (Object.keys(mapped).length) setProblems(mapped);
        else setBanner('failed');
      } else setBanner('failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!invoice) return;
    setBusy(true);
    try {
      const result = await salesApi.remove(returnId, invoice.id, invoice.version);
      onDeleted?.(result.return);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      const code = error.problem?.code;
      setBanner(
        code === 'VERSION_MISMATCH'
          ? 'conflict'
          : code === 'RETURN_FILED'
            ? 'filed'
            : code === 'RETURN_BEING_FILED'
              ? 'filing'
              : 'failed',
      );
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const found = taxpayer ? (
    <p
      className={`field__help ${cancelled || taxpayer.status === 'suspended' ? 'field__help--warn' : 'field__help--ok'}`}
    >
      <Icon name={cancelled || taxpayer.status === 'suspended' ? 'alert-triangle' : 'check-circle'} size={16} />
      {cancelled
        ? t('sales.form.buyerCancelled', {
            name: taxpayer.tradeName ?? taxpayer.legalName,
            date: taxpayer.cancelledOn ? shortDate(taxpayer.cancelledOn) : '',
          })
        : taxpayer.status === 'suspended'
          ? t('sales.form.buyerSuspended', { name: taxpayer.tradeName ?? taxpayer.legalName })
          : t('sales.form.buyerFound', { name: taxpayer.tradeName ?? taxpayer.legalName, state: taxpayer.stateName })}
    </p>
  ) : notRegistered ? (
    <p className="field__help field__help--error">{t('sales.form.buyerNotFound')}</p>
  ) : unchecked ? (
    <p className="field__help">{t('sales.form.buyerUnchecked')}</p>
  ) : null;

  const savedBuyers = (buyers.data?.buyers ?? []).filter((b) => b.gstin !== buyerGstin).slice(0, 4);

  return (
    <form id={formId} className="sale-form" noValidate onSubmit={submit}>
      <h2 className="sheet__title">
        {heading ??
          (correcting
            ? t('sales.correct.title', { number: correcting.numbers.join(', ') })
            : invoice
              ? t('sales.form.editTitle', { number: invoice.numbers.join(', ') })
              : t('sales.form.addTitle'))}
      </h2>
      {lead}
      {correcting ? (
        correcting.lines.length > 1 ? (
          <Callout tone="amber" icon="alert-triangle">
            {t('sales.correct.manyItems', { count: correcting.lines.length })}
          </Callout>
        ) : (
          <Callout icon="pencil">{t('sales.correct.lead')}</Callout>
        )
      ) : null}
      {message ? (
        <Callout tone="amber" icon="alert-triangle" role="alert">
          {message}
        </Callout>
      ) : null}
      {toast ? (
        <p className="sale-form__toast" role="status">
          <Icon name="check-circle" size={16} />
          {toast}
        </p>
      ) : null}
      {banner ? (
        <Callout tone={banner === 'failed' ? 'error' : 'amber'} icon="alert-triangle">
          {t(`sales.form.${banner}`)}
        </Callout>
      ) : null}

      {correcting ? null : (
        <fieldset className="filter-tabs" aria-label={t('sales.form.kind')}>
          {(['b2b', 'b2c'] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              className="filter-tabs__option"
              aria-pressed={values.kind === kind}
              onClick={() => set('kind', kind)}
            >
              {t(`sales.form.${kind}`)}
            </button>
          ))}
        </fieldset>
      )}

      {correcting?.buyer ? (
        <LockedRow
          label={t('sales.form.gstin')}
          value={
            correcting.buyer.name ? `${correcting.buyer.name} · ${correcting.buyer.gstin}` : correcting.buyer.gstin
          }
          help={t('sales.correct.asFiled')}
        />
      ) : values.kind === 'b2b' ? (
        <div className="sale-form__buyer">
          <GstinField
            value={values.gstin}
            onChange={(gstin) => set('gstin', gstin)}
            inputRef={firstField}
            label={t('sales.form.gstin')}
            placeholder="29AAGCM4412K1ZB"
            found={found}
            error={problems.gstin ?? null}
          />
          {!values.gstin && savedBuyers.length ? (
            <fieldset className="chips">
              <legend className="visually-hidden">{t('sales.form.savedBuyers')}</legend>
              {savedBuyers.map((b) => (
                <button key={b.gstin} type="button" className="chip" onClick={() => set('gstin', b.gstin)}>
                  {b.name}
                </button>
              ))}
            </fieldset>
          ) : null}
        </div>
      ) : (
        <SelectRow
          label={t('sales.form.placeOfSupply')}
          value={values.delivery || sellerState}
          error={problems.delivery}
          onChange={(code) => set('delivery', code)}
          options={Object.entries(GST_STATES)
            .filter(([code]) => code !== '97' && code !== '99')
            .sort((a, b) => (a[0] === sellerState ? -1 : b[0] === sellerState ? 1 : a[1].localeCompare(b[1])))
            .map(([code, name]) => ({
              value: code,
              label: code === sellerState ? t('sales.form.sameState', { state: name }) : name,
            }))}
        />
      )}

      <div className="fields-row">
        {correcting ? (
          <LockedRow label={t('sales.form.number')} value={correcting.numbers.join(', ')} />
        ) : (
          <TextRow
            label={t('sales.form.number')}
            value={values.number}
            error={problems.number}
            onChange={(v) => set('number', v.toUpperCase())}
            placeholder="INV-2404"
            maxLength={16}
            {...(values.kind === 'b2c' ? { inputRef: firstField } : {})}
          />
        )}
        <TextRow
          label={t('sales.form.date')}
          type="date"
          value={values.date}
          error={problems.date}
          onChange={(v) => set('date', v)}
          max={indiaToday()}
        />
      </div>

      <div className="fields-row">
        <HsnField
          value={values.hsn}
          name={values.hsnName}
          date={values.date || defaultDate(draft.period)}
          error={problems.hsn}
          onType={(code) => {
            if (code !== values.hsn) {
              set('hsn', code);
              setValues((v) => ({ ...v, hsnName: '' }));
            }
          }}
          onPick={(result) => {
            setValues((v) => ({
              ...v,
              hsn: result.code,
              hsnName: result.description,
              rate: !rateTouched && result.rate !== null ? String(result.rate) : v.rate,
            }));
            setPriceRule(result.priceRule);
            setProblems((p) => ({ ...p, hsn: undefined, rate: undefined }));
          }}
        />
        <SelectRow
          label={t('sales.form.rate')}
          value={values.rate}
          error={problems.rate}
          placeholder="—"
          onChange={(v) => {
            set('rate', v);
            setRateTouched(true);
          }}
          options={rateOptions.common.map((r) => ({ value: String(r), label: `${r}%` }))}
          more={
            rateOptions.rest.length
              ? {
                  label: t('sales.form.otherRates'),
                  options: rateOptions.rest.map((r) => ({ value: String(r), label: `${r}%` })),
                }
              : undefined
          }
        />
      </div>
      {priceRule && rate !== null ? (
        <p className="field__help sale-form__rule">
          {t('sales.form.ratePriceRule', {
            rate: values.rate,
            limit: money(priceRule.priceLimit),
            above: priceRule.rateAbove,
          })}
        </p>
      ) : null}

      <div className="fields-row">
        {nature === 'goods' ? (
          <TextRow
            label={t('sales.form.quantity')}
            value={values.quantity}
            error={problems.quantity}
            onChange={(v) => set('quantity', v.replace(/[^\d.]/g, ''))}
            inputMode="decimal"
            narrow
          />
        ) : null}
        <TextRow
          label={values.includesGst ? t('sales.form.amountWithGst') : t('sales.form.amount')}
          value={values.amount}
          error={problems.amount}
          onChange={(v) => set('amount', v)}
          placeholder="1,20,000"
          help={t('sales.form.amountHelp')}
          inputMode="text"
        />
      </div>
      <label className="check-row sale-form__gst">
        <input type="checkbox" checked={values.includesGst} onChange={(e) => set('includesGst', e.target.checked)} />
        <span>{t('sales.form.includesGst')}</span>
      </label>

      {tax && taxable !== null && invoiceValue !== null && rate !== null && pos ? (
        <div className="sale-preview" aria-live="polite">
          <span className="sale-preview__route">
            {t('sales.form.route', {
              from: state(sellerState),
              to: state(pos),
              how: inter ? t('sales.form.howInter') : t('sales.form.howSame'),
            })}
          </span>
          {values.includesGst ? (
            <span className="sale-preview__row">
              <span>{t('sales.form.amount')}</span>
              <span className="sale-preview__value">{money(taxable)}</span>
            </span>
          ) : null}
          <span className="sale-preview__row">
            <span>{inter ? t('sales.form.igst', { rate }) : t('sales.form.cgstSgst', { half: rate / 2 })}</span>
            <span className="sale-preview__value">
              {inter ? money(tax.igst) : `${money(tax.cgst)} + ${money(tax.sgst)}`}
            </span>
          </span>
          <span className="sale-preview__row sale-preview__row--total">
            <span>{t('sales.form.invoiceValue')}</span>
            <span className="sale-preview__total">{money(invoiceValue)}</span>
          </span>
          {table ? <span className="sale-preview__table">{t(`sales.form.table.${table}`)}</span> : null}
        </div>
      ) : null}

      {invoice ? (
        confirmDelete ? (
          <div className="sale-form__delete" role="alert">
            <p>
              {t(correcting ? 'sales.correct.undoConfirm' : 'sales.form.deleteConfirm', {
                number: invoice.numbers.join(', '),
              })}
            </p>
            <div className="sale-form__delete-actions">
              <Button variant="danger" inline small onClick={remove} disabled={busy}>
                {t(correcting ? 'sales.correct.undoYes' : 'sales.form.deleteYes')}
              </Button>
              <Button variant="ghost" inline small onClick={() => setConfirmDelete(false)}>
                {t('sales.form.deleteNo')}
              </Button>
            </div>
          </div>
        ) : (
          <button type="button" className="linkbtn sale-form__delete-link" onClick={() => setConfirmDelete(true)}>
            <Icon name={correcting ? 'undo' : 'trash'} size={16} />
            {t(correcting ? 'sales.correct.undo' : 'sales.form.delete')}
          </button>
        )
      ) : null}
    </form>
  );
}

/** A field that can't change here (GSTR-1A keeps a filed sale's buyer and number), with why. */
function LockedRow({ label, value, help }: { label: string; value: string; help?: string }) {
  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <p className="field__locked">
        <Icon name="lock" size={16} />
        <span>{value}</span>
      </p>
      {help ? <p className="field__help">{help}</p> : null}
    </div>
  );
}

export function TextRow({
  label,
  value,
  onChange,
  error,
  help,
  type = 'text',
  placeholder,
  maxLength,
  max,
  inputMode,
  inputRef,
  narrow,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  help?: string;
  type?: 'text' | 'date';
  placeholder?: string;
  maxLength?: number;
  max?: string;
  inputMode?: 'text' | 'decimal' | 'numeric';
  inputRef?: React.Ref<HTMLInputElement>;
  narrow?: boolean;
}) {
  const id = useId();
  const helpId = `${id}-help`;
  const message = error ?? help;
  return (
    <div className={`field${narrow ? ' field--narrow' : ''}`}>
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        ref={inputRef}
        className="input"
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        max={max}
        inputMode={inputMode}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={error ? true : undefined}
        aria-describedby={message ? helpId : undefined}
      />
      {message ? (
        <p id={helpId} className={`field__help${error ? ' field__help--error' : ''}`} aria-live="polite">
          {message}
        </p>
      ) : null}
    </div>
  );
}

export function SelectRow({
  label,
  value,
  onChange,
  options,
  more,
  error,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  more?: { label: string; options: { value: string; label: string }[] } | undefined;
  error?: string | undefined;
  placeholder?: string;
}) {
  const id = useId();
  const helpId = `${id}-help`;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="input select"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? helpId : undefined}
      >
        {placeholder !== undefined && value === '' ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
        {more ? (
          <optgroup label={more.label}>
            {more.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
      {error ? (
        <p id={helpId} className="field__help field__help--error" aria-live="polite">
          {error}
        </p>
      ) : null}
    </div>
  );
}
